/**
 * Ported VERBATIM from apr_template `src/pages/ResourceCapacity.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './ApplicationDependencies.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_COLOR, WS_RADIUS, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const getTodayISO = () => new Date().toISOString().split('T')[0];

const ResourceCapacityHelp: Help = {
  title: 'Resource Capacity',
  icon: '👥',
  purpose:
    'Define the <strong>resource pools</strong> available for delivery &mdash; each a skillset at a location, with a daily cost and an effective date range. These pools are what you allocate to phases.',
  doHere: [
    'Click <strong>&#10133; Add Resource Pool</strong> to create a pool: pick a <strong>Resource Skillset</strong>, set cost/day, location and effective dates.',
    'Bulk-load pools via <strong>&#128203; Download Template</strong> then <strong>&#128228; Upload File</strong>.',
    'Filter by resource type or location; <strong>&#128229; Download Data</strong> exports the list.',
    'Use <strong>&#128065;</strong> / <strong>&#9999;&#65039;</strong> / <strong>&#128465;&#65039;</strong> to view, edit or delete a pool.',
  ],
  fits: 'Integration &rarr; Resource Planning &rarr; Resource Capacity',
  firstTime:
    'Add a resource pool for each skillset you will staff, choosing On-Site or Off-Shore and a cost per day.',
  sections: [
    {
      type: 'defs',
      label: 'Location',
      items: [
        { term: 'On-Site', color: '#1565c0', def: 'Resources working on-premise / local, usually at a higher day rate.' },
        { term: 'Off-Shore', color: '#2e7d32', def: 'Remote / offshore resources, typically at a lower day rate.' },
      ],
    },
  ],
  links: [
    { href: '/integration-framework/resource-skillsets', label: 'Resource Skillsets' },
    { href: '/integration-framework/resource-allocation', label: 'Resource Allocation' },
  ],
};

interface ResourceCapacityRecord {
  resource_pool_id: number;
  resource_type: string;
  cost_per_day: number;
  resource_location: 'On-Site' | 'Off-Shore';
  effective_start_date: string;
  effective_end_date: string;
  created_at?: string;
  updated_at?: string;
}

interface SkillsetMaster {
  skillset_id: string;
  skillset_name: string;
  skillset_description: string;
}

const ResourceCapacity: React.FC = () => {
  const [records, setRecords] = useState<ResourceCapacityRecord[]>([]);
  const [skillsets, setSkillsets] = useState<SkillsetMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterResourceType, setFilterResourceType] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<ResourceCapacityRecord | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const { confirm, notify, dialogs } = useWsDialogs();
  const [formData, setFormData] = useState({
    resourcePoolId: '',
    resourceType: '',
    costPerDay: '',
    resourceLocation: 'On-Site',
    effectiveStartDate: getTodayISO(),
    effectiveEndDate: '9999-12-31'
  });

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/resource-capacity');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      // Sort by Resource Pool ID in ascending order
      data.sort((a: ResourceCapacityRecord, b: ResourceCapacityRecord) => a.resource_pool_id - b.resource_pool_id);
      setRecords(data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load resource capacity records');
      setLoading(false);
    }
  };

  const fetchSkillsets = async () => {
    try {
      const response = await authFetch('/api/skillset-master');
      if (!response.ok) throw new Error('Failed to fetch skillsets');
      const result = await response.json();
      setSkillsets(result.data || []);
    } catch (err: any) {
      console.error('Failed to load skillsets:', err);
      setSkillsets([]);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchSkillsets();
  }, []);


  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0]!.split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i]!.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/resource-capacity/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} resource capacity records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Resource Type,Cost Per Day,Resource Location,Effective Start Date,Effective End Date
Cloud Architect,1200,On-Site,2025-01-01,9999-12-31
DevOps Engineer,950,Off-Shore,2025-01-01,9999-12-31
AWS Architect,1100,On-Site,2025-01-01,9999-12-31
Scrum Master,850,On-Site,2025-01-01,9999-12-31
QA Engineer,700,Off-Shore,2025-01-01,9999-12-31
Business Analyst,750,On-Site,2025-01-01,9999-12-31`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resource_capacity_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      notify('No data to download', 'info');
      return;
    }

    // Match template format: Resource Type,Cost Per Day,Resource Location,Effective Start Date,Effective End Date
    const headers = ['Resource Type', 'Cost Per Day', 'Resource Location', 'Effective Start Date', 'Effective End Date'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.resource_type || '').replace(/"/g, '""')}"`,
        record.cost_per_day || 0,
        `"${(record.resource_location || '').replace(/"/g, '""')}"`,
        record.effective_start_date || '2025-01-01',
        record.effective_end_date || '9999-12-31'
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_capacity_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate that the resource type exists in skillsets
    if (formData.resourceType) {
      const skillsetExists = skillsets.some(skillset =>
        skillset.skillset_name.toLowerCase() === formData.resourceType.toLowerCase()
      );

      if (!skillsetExists) {
        setError(`Resource Skillset "${formData.resourceType}" does not exist in the Resource Skillsets table. Please add it first or select an existing one.`);
        return;
      }
    }

    // Validate date range
    if (formData.effectiveStartDate && formData.effectiveEndDate) {
      const startDate = new Date(formData.effectiveStartDate);
      const endDate = new Date(formData.effectiveEndDate);
      if (endDate < startDate) {
        setError('Effective End Date must be after Effective Start Date');
        return;
      }
    }

    try {
      const payload = {
        resource_pool_id: formData.resourcePoolId ? parseInt(formData.resourcePoolId) : null,
        resource_type: formData.resourceType,
        cost_per_day: formData.costPerDay ? parseFloat(formData.costPerDay) : 0,
        resource_location: formData.resourceLocation || 'On-Site',
        effective_start_date: formData.effectiveStartDate || getTodayISO(),
        effective_end_date: formData.effectiveEndDate || '9999-12-31'
      };

      if (editingRecord) {
        const response = await authFetch(`/api/resource-capacity/${editingRecord.resource_pool_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to update');
        setSuccess('Resource capacity record updated successfully');
      } else {
        const response = await authFetch('/api/resource-capacity', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to create');
        setSuccess('Resource capacity record created successfully');
      }

      fetchRecords();
      
      // Close modal and reset form after successful save
      setShowForm(false);
      setEditingRecord(null);
      resetFormData();
      
      // Clear any error messages
      setError('');
    } catch (err: any) {
      console.error('Failed to save resource capacity record:', err);
      setError(err.message || 'Failed to save resource capacity record');
    }
  };

  const resetFormData = () => {
    setFormData({
      resourcePoolId: '',
      resourceType: '',
      costPerDay: '',
      resourceLocation: 'On-Site',
      effectiveStartDate: getTodayISO(),
      effectiveEndDate: '9999-12-31'
    });
  };

  const handleEdit = (record: ResourceCapacityRecord) => {
    setEditingRecord(record);
    setViewMode(false);
    setFormData({
      resourcePoolId: record.resource_pool_id.toString(),
      resourceType: record.resource_type,
      costPerDay: record.cost_per_day?.toString() || '',
      resourceLocation: record.resource_location || 'On-Site',
      effectiveStartDate: record.effective_start_date || getTodayISO(),
      effectiveEndDate: record.effective_end_date || '9999-12-31'
    });
    setShowForm(true);
  };

  const handleView = (record: ResourceCapacityRecord) => {
    setEditingRecord(record);
    setViewMode(true);
    setFormData({
      resourcePoolId: record.resource_pool_id.toString(),
      resourceType: record.resource_type,
      costPerDay: record.cost_per_day?.toString() || '',
      resourceLocation: record.resource_location || 'On-Site',
      effectiveStartDate: record.effective_start_date || getTodayISO(),
      effectiveEndDate: record.effective_end_date || '9999-12-31'
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (await confirm('Are you sure you want to delete this resource capacity record?', { tone: 'destructive' })) {
      try {
        const response = await authFetch(`/api/resource-capacity/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Resource capacity record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete resource capacity record');
      }
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    resetFormData();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setViewMode(false);
    resetFormData();
    setError('');
  };


  if (loading) return <div className="md-loading">Loading resource capacity records...</div>;
  if (error && !records.length) return <div className="md-error">Error: {error}</div>;

  const filteredRecords = records.filter(record => {
    const typeMatch = filterResourceType === '' || 
      record.resource_type.toLowerCase().includes(filterResourceType.toLowerCase());
    const locationMatch = filterLocation === '' || 
      record.resource_location === filterLocation;
    return typeMatch && locationMatch;
  });

  // DW: field look (36px, 8px corners, 1px outline, 14px) comes from pp-workspace.css; only
  // width and the read-only fill stay inline.
  const fieldStyle: React.CSSProperties = { width: '100%', backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', ...wsType('label') };
  const cellStyle = (textAlign: 'left' | 'center'): React.CSSProperties => ({ textAlign, whiteSpace: 'nowrap' });

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <HelpPanel help={ResourceCapacityHelp} />
      {/* Header - Fixed/Sticky */}
      <PageHeader title={<>Resource Capacity Management</>} subtitle={<>Manage resource pools, capacity planning, and availability by resource type</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new resource capacity record"
            >
              <Icon icon={icons.Plus} size={20} /> Add Resource Pool
            </button>
            <button
              className="btn"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              <Icon icon={icons.ClipboardList} size={20} /> Download Template
            </button>
            <button
              className="btn"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import resource records from CSV"
            >
              <Icon icon={icons.Upload} size={20} /> Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn"
                onClick={downloadData}
                title="Download current data as CSV"
              >
                <Icon icon={icons.Download} size={20} /> Download Data
              </button>
            )}
      </div>

      {/* Scrollable Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {/* Filter Section */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ ...wsType('label'), minWidth: '120px' }}>Filter by Type:</label>
            <input
              type="text"
              value={filterResourceType}
              onChange={(e) => setFilterResourceType(e.target.value)}
              placeholder="Enter resource type to filter..."
              style={{ minWidth: '200px' }}
            />
            <label style={{ ...wsType('label'), minWidth: '120px' }}>Filter by Location:</label>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All Locations</option>
              <option value="On-Site">On-Site</option>
              <option value="Off-Shore">Off-Shore</option>
            </select>
            {(filterResourceType || filterLocation) && (
              <button
                className="btn"
                onClick={() => {
                  setFilterResourceType('');
                  setFilterLocation('');
                }}
              >
                Clear Filters
              </button>
            )}
            <span style={{ ...wsType('label'), color: WS_COLOR.textMuted }}>
              Showing {filteredRecords.length} of {records.length} resource pools
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ flex: '1', overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon"><Icon icon={icons.Users} size={24} /></div>
            <h3>No Resource Capacity Records Found</h3>
            <p>Use the action cards above to add resource pools or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table">
              <thead>
                <tr>
                  <th style={cellStyle('left')}>Resource Skillset</th>
                  <th style={cellStyle('center')}>Cost/Day</th>
                  <th style={cellStyle('center')}>Resource Location</th>
                  <th style={cellStyle('center')}>Effective Start Date</th>
                  <th style={cellStyle('center')}>Effective End Date</th>
                  <th style={cellStyle('center')}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => (
                  <tr key={record.resource_pool_id}>
                    <td style={cellStyle('left')}>
                      {record.resource_type}
                    </td>
                    <td style={cellStyle('center')}>
                      ${record.cost_per_day || 0}
                    </td>
                    <td style={cellStyle('center')}>
                      {record.resource_location}
                    </td>
                    <td style={cellStyle('center')}>
                      {record.effective_start_date ? new Date(record.effective_start_date).toLocaleDateString() : '01/01/2025'}
                    </td>
                    <td style={cellStyle('center')}>
                      {record.effective_end_date ? new Date(record.effective_end_date).toLocaleDateString() : '12/31/9999'}
                    </td>
                    <td style={cellStyle('center')}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                          icon={icons.Eye}
                          label="View details"
                          title="View details"
                          onClick={() => handleView(record)}
                        />
                        <IconButton
                          icon={icons.Pencil}
                          label="Edit resource pool"
                          title="Edit resource pool"
                          onClick={() => handleEdit(record)}
                        />
                        <IconButton
                          icon={icons.Trash2}
                          label="Delete resource pool"
                          title="Delete resource pool"
                          onClick={() => handleDelete(record.resource_pool_id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>{/* End Scrollable Content Area */}

      {/* Resource Capacity Form — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showForm}
        title={viewMode ? 'View Resource Pool' : editingRecord ? 'Edit Resource Pool' : 'Add New Resource Pool'}
        onClose={handleCloseForm}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={handleCloseForm}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" form="rc-pool-form" className="btn btn-primary">
                {editingRecord ? 'Update Resource Pool' : 'Create Resource Pool'}
              </button>
            )}
          </>
        }
      >
              <form id="rc-pool-form" onSubmit={handleSubmit}>
                {error && (
                  <div style={{ marginBottom: '16px' }}>
                    <Banner tone="error">{error}</Banner>
                  </div>
                )}

                {editingRecord && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>Resource Pool ID</label>
                    <input
                      type="text"
                      className="md-input"
                      value={formData.resourcePoolId}
                      disabled
                      style={{ width: '100%', backgroundColor: WS_COLOR.surfaceSubtle }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Resource Skillset *</label>
                  <select
                    className="md-input"
                    value={formData.resourceType}
                    onChange={(e) => setFormData({...formData, resourceType: e.target.value})}
                    required
                    disabled={viewMode}
                    style={fieldStyle}
                  >
                    <option value="">Select a skillset...</option>
                    {skillsets.map(skillset => (
                      <option key={skillset.skillset_id} value={skillset.skillset_name}>
                        {skillset.skillset_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Cost Per Day</label>
                  <input
                    type="text"
                    className="md-input"
                    value={formData.costPerDay}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFormData({...formData, costPerDay: value});
                    }}
                    placeholder="0"
                    disabled={viewMode}
                    style={fieldStyle}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Resource Location *</label>
                  <select
                    className="md-input"
                    value={formData.resourceLocation}
                    onChange={(e) => setFormData({...formData, resourceLocation: e.target.value as 'On-Site' | 'Off-Shore'})}
                    required
                    disabled={viewMode}
                    style={fieldStyle}
                  >
                    <option value="On-Site">On-Site</option>
                    <option value="Off-Shore">Off-Shore</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Effective Start Date *</label>
                    <input
                      type="date"
                      className="md-input"
                      value={formData.effectiveStartDate}
                      onChange={(e) => setFormData({...formData, effectiveStartDate: e.target.value})}
                      required
                      disabled={viewMode}
                      style={fieldStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Effective End Date *</label>
                    <input
                      type="date"
                      className="md-input"
                      value={formData.effectiveEndDate}
                      onChange={(e) => setFormData({...formData, effectiveEndDate: e.target.value})}
                      required
                      disabled={viewMode}
                      style={fieldStyle}
                    />
                  </div>
                </div>

                {/* Cancel/Close and Create/Update are in the panel's fixed action row (Sheet `actions`). */}
              </form>
      </Sheet>

      {/* Upload preview — a right-side panel; double width because it holds the CSV preview table. */}
      <Sheet
        open={showUploadModal}
        title="Preview Import Data"
        onClose={() => setShowUploadModal(false)}
        width={1062}
        actions={
          <>
            <button type="button" className="btn" onClick={() => setShowUploadModal(false)}>
              Cancel
            </button>
            {previewData.length > 0 && (
              <button type="button" className="btn btn-primary" onClick={handleBulkImport}>
                Import {previewData.length} Records
              </button>
            )}
          </>
        }
      >
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field }}>
                    <table style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ textAlign: 'left' }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
      </Sheet>

      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {dialogs}
    </div>
  );
};

export default ResourceCapacity;